import { Card } from './types';
import { CardType } from './validation';
import { storyIssues, StoryCard } from './storyGraph';
import { asArray } from './listOps';

/**
 * Editor metadata for every lesson card type: a label, a template for "Add step", and a
 * field schema the visual editor renders generically. Card shapes follow
 * alpha-web-fe/types/lesson.ts. Types with a structure a plain form can't express well
 * (scenario, chat_simulator, story_mode, drag_rank, step_chat) get a custom editor in
 * editors/ on top of their schema fields, plus custom `issues`.
 *
 * Kept free of React so it is unit-testable.
 */

export type CardGroup = 'step' | 'test';

interface FieldBase {
  key: string;
  label: string;
  /** Required fields are flagged in the step's issue list while blank. */
  required?: boolean;
  /**
   * The key may be absent (`?:` in lesson.ts), so clearing the field removes it. Any other
   * field keeps its key when cleared (`''`, `[]`) — the apps read those unguarded.
   */
  optional?: boolean;
  placeholder?: string;
  help?: string;
}

export interface SelectOption {
  value: string;
  label: string;
  color?: string;
}

export type FieldSpec =
  /** `richtext` is multi-line and supports **bold** (Ctrl/⌘+B). */
  | (FieldBase & { kind: 'text' | 'richtext' | 'emoji' | 'color' })
  | (FieldBase & { kind: 'number'; min?: number; suffix?: string })
  | (FieldBase & { kind: 'boolean'; on: string; off: string })
  | (FieldBase & { kind: 'select'; options: SelectOption[] })
  | (FieldBase & { kind: 'stringList'; itemLabel: string; rich?: boolean })
  | (FieldBase & {
      kind: 'objectList';
      itemLabel: string;
      fields: FieldSpec[];
      create: () => Record<string, any>;
      /** Field holding a per-item id that must stay unique (assigned on add / duplicate). */
      idKey?: string;
      idPrefix?: string;
    })
  /** A nested object, e.g. before_after's `bad: { text, emoji }`. */
  | (FieldBase & { kind: 'object'; fields: FieldSpec[]; tone?: 'good' | 'bad' })
  /** Layout only: renders its fields side by side on the same object. */
  | { kind: 'row'; fields: FieldSpec[]; columns?: string };

export interface CardTypeMeta {
  type: CardType;
  label: string;
  group: CardGroup;
  description: string;
  fields: FieldSpec[];
  /** Rendered after the type's custom editor. */
  fieldsAfter?: FieldSpec[];
  create: () => Card;
  issues?: (card: Card) => string[];
}

// ─── Field factories ───

type Extra = Partial<Omit<FieldBase, 'key' | 'label'>>;
const OPTIONAL = { optional: true };

const text = (key: string, label: string, extra: Extra = {}): FieldSpec => ({ kind: 'text', key, label, ...extra });
const rich = (key: string, label: string, extra: Extra = {}): FieldSpec => ({ kind: 'richtext', key, label, ...extra });
const emoji = (key: string, label = 'Emoji', extra: Extra = {}): FieldSpec => ({ kind: 'emoji', key, label, ...extra });
const color = (key: string, label = 'Color', extra: Extra = {}): FieldSpec => ({ kind: 'color', key, label, ...extra });
const row = (fields: FieldSpec[], columns?: string): FieldSpec => ({ kind: 'row', fields, columns });
const strings = (key: string, label: string, itemLabel: string, extra: Extra & { rich?: boolean } = {}): FieldSpec =>
  ({ kind: 'stringList', key, label, itemLabel, ...extra });

const TITLE = text('title', 'Title', { required: true });
const EMOJI_TITLE = row([emoji('emoji'), TITLE], '96px 1fr');
const ICON_COLUMNS = '96px 1fr 1fr';
const DEFAULT_COLOR = '#60A5FA';

// ─── Ids ───

export function optionLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

export function uniqueId(existing: string[], prefix: string): string {
  let n = existing.length + 1;
  while (existing.includes(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

/** Next unused single-letter id (a, b, c…) — the convention for scenario options and chat choices. */
export function nextLetterId(existing: string[]): string {
  for (let i = 0; i < 26; i++) {
    const id = String.fromCharCode(97 + i);
    if (!existing.includes(id)) return id;
  }
  return uniqueId(existing, 'opt_');
}

/**
 * Re-letters scenario options a, b, c… by position, moving each explanation with its option.
 * The apps label an option by its id, so this keeps the letters in order after a move or a
 * delete. Explanations that belong to no option are dropped — nothing can show them.
 */
export function relabelOptions<T extends { id: string }>(
  options: T[],
  explanations: Record<string, string>,
): { options: T[]; explanations: Record<string, string> } {
  if (options.length > 26) return { options, explanations };
  const nextExplanations: Record<string, string> = {};
  const nextOptions = options.map((option, i) => {
    const id = String.fromCharCode(97 + i);
    if (explanations[option.id] !== undefined) nextExplanations[id] = explanations[option.id];
    return { ...option, id };
  });
  return { options: nextOptions, explanations: nextExplanations };
}

/** The letter the apps show for a scenario option: its id, upper-cased. */
export function scenarioOptionLabel(option: { id?: unknown }, index: number): string {
  return typeof option?.id === 'string' && option.id ? option.id.toUpperCase() : optionLetter(index);
}

// ─── Custom issue checks ───

const isFilled = (value: unknown) =>
  value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '');


function scenarioIssues(card: Card): string[] {
  const options = asArray(card.options);
  const explanations = card.explanations ?? {};
  if (options.length === 0) return ['Add at least one answer option'];
  const issues: string[] = [];
  if (!options.some((o) => o?.correct)) issues.push('No option is marked correct');
  options.forEach((option, i) => {
    const label = scenarioOptionLabel(option, i);
    if (!isFilled(option?.text)) issues.push(`Option ${label} is empty`);
    if (!isFilled(explanations[option?.id])) issues.push(`Option ${label} has no explanation`);
  });
  return issues;
}

function chatSimulatorIssues(card: Card): string[] {
  const issues: string[] = [];
  const opening = asArray(card.initialMessages);
  if (opening.length === 0) issues.push('Add at least one opening message');
  opening.forEach((m, i) => {
    if (!isFilled(m.text)) issues.push(`Opening message ${i + 1} is empty`);
  });

  const choices = asArray(card.choices);
  if (choices.length === 0) return [...issues, 'Add at least one reply choice'];
  choices.forEach((choice, i) => {
    const name = `Choice ${optionLetter(i)}`;
    const replies = asArray(choice.herReply);
    if (!isFilled(choice.text)) issues.push(`${name} is empty`);
    if (replies.length === 0) issues.push(`${name} has no replies from her`);
    replies.forEach((r, j) => {
      if (!isFilled(r.text)) issues.push(`${name}: reply ${j + 1} is empty`);
    });
    if (!isFilled(choice.verdict)) issues.push(`${name} has no verdict`);
  });
  if (!choices.some((c) => c.quality === 'good')) issues.push('No choice is marked good');
  return issues;
}

function stepChatIssues(card: Card): string[] {
  const messages = asArray(card.messages);
  if (messages.length === 0) return ['Add at least one message'];
  return messages.flatMap((m, i) => (isFilled(m.text) ? [] : [`Message ${i + 1} is empty`]));
}

export function dragRankOrderMatches(card: Card): boolean {
  const ids = asArray(card.items).map((item) => item.id);
  const order = asArray(card.correctOrder);
  return order.length === ids.length && new Set(order).size === order.length && order.every((id) => ids.includes(id));
}

function dragRankIssues(card: Card): string[] {
  const items = asArray(card.items);
  if (items.length === 0) return ['Add at least one item'];
  const issues = items.flatMap((item, i) => (isFilled(item.text) ? [] : [`Item ${i + 1} is empty`]));
  if (!dragRankOrderMatches(card)) {
    issues.push('The correct order doesn\'t match the items — reorder them once to fix it');
  }
  return issues;
}

// ─── Card types ───

const META: CardTypeMeta[] = [
  // Steps
  {
    type: 'intro', label: 'Intro', group: 'step',
    description: 'Opening card: title, body and an optional highlighted line.',
    fields: [TITLE, rich('body', 'Body', { required: true }), rich('highlight', 'Highlight', OPTIONAL), rich('note', 'Note', OPTIONAL)],
    create: () => ({ type: 'intro', title: '', body: '' }),
  },
  {
    type: 'insight', label: 'Insight', group: 'step',
    description: 'A key idea with an optional note.',
    fields: [TITLE, rich('body', 'Body', { required: true }), rich('note', 'Note', OPTIONAL)],
    create: () => ({ type: 'insight', title: '', body: '' }),
  },
  {
    type: 'strategy', label: 'Strategy', group: 'step',
    description: 'A technique to apply, with an optional highlight and note.',
    fields: [TITLE, rich('body', 'Body', { required: true }), rich('highlight', 'Highlight', OPTIONAL), rich('note', 'Note', OPTIONAL)],
    create: () => ({ type: 'strategy', title: '', body: '' }),
  },
  {
    type: 'mentor', label: 'Mentor', group: 'step',
    description: 'Advice from the mentor. This card has no title.',
    fields: [rich('body', 'Body', { required: true }), rich('note', 'Note', OPTIONAL)],
    create: () => ({ type: 'mentor', body: '' }),
  },
  {
    type: 'summary', label: 'Summary', group: 'step',
    description: 'Lesson wrap-up with bullet points.',
    fields: [
      row([emoji('emoji', 'Emoji', OPTIONAL), TITLE], '96px 1fr'),
      strings('bullets', 'Bullets', 'bullet', { required: true, rich: true }),
      rich('body', 'Body', OPTIONAL),
    ],
    create: () => ({ type: 'summary', emoji: '🎯', title: '', bullets: [''] }),
  },
  {
    type: 'challenge', label: 'Challenge', group: 'step',
    description: 'A real-life task the user accepts.',
    fields: [TITLE, rich('challengeText', 'Challenge', { required: true }), rich('challengeTip', 'Tip', { required: true })],
    create: () => ({ type: 'challenge', title: '', challengeText: '', challengeTip: '' }),
  },
  {
    type: 'comic', label: 'Comic', group: 'step',
    description: 'Comic panels with captions, thoughts and a final takeaway.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'panels', label: 'Panels', itemLabel: 'Panel', required: true,
        create: () => ({ scene: '', panelBg: '#1a1a2e', mood: '' }),
        fields: [
          row([
            text('scene', 'Scene', { required: true, placeholder: '🚶‍♂️🌳🚶‍♀️' }),
            emoji('mood', 'Mood'),
            color('panelBg', 'Background', { required: true }),
          ], '1fr 96px 1fr'),
          row([text('tag', 'Tag', { placeholder: 'СЦЕНА 1', optional: true }), color('tagColor', 'Tag color', OPTIONAL)]),
          rich('caption', 'Caption', OPTIONAL),
          rich('thought', 'Thought', { optional: true, help: 'Italic inner thought in an amber bubble' }),
          rich('insight', 'Insight', { optional: true, help: 'Key insight in an accent box' }),
          rich('example', 'Example', { optional: true, help: 'Example message / action in a green box' }),
          strings('takeaway', 'Takeaway', 'line', { optional: true, help: 'Usually on the last panel; the first line is shown large' }),
        ],
      },
    ],
    create: () => ({ type: 'comic', title: '', panels: [{ scene: '', panelBg: '#1a1a2e', mood: '' }] }),
  },
  {
    type: 'mindmap', label: 'Mind map', group: 'step',
    description: 'A center topic with branches the user opens one by one.',
    fields: [
      TITLE,
      row([emoji('centerIcon', 'Center icon'), text('centerLabel', 'Center label', { required: true })], '96px 1fr'),
      {
        kind: 'objectList', key: 'branches', label: 'Branches', itemLabel: 'Branch', required: true,
        idKey: 'id', idPrefix: 'b',
        create: () => ({ label: '', icon: '', color: DEFAULT_COLOR, items: [''], tip: '' }),
        fields: [
          row([emoji('icon', 'Icon'), text('label', 'Label', { required: true }), color('color')], ICON_COLUMNS),
          strings('items', 'Items', 'item', { rich: true }),
          rich('tip', 'Tip'),
        ],
      },
    ],
    create: () => ({
      type: 'mindmap', title: '', centerIcon: '', centerLabel: '',
      branches: [{ id: 'b1', label: '', icon: '', color: DEFAULT_COLOR, items: [''], tip: '' }],
    }),
  },
  {
    type: 'stats', label: 'Stats', group: 'step',
    description: 'Big numbers with an explanation, one slide each.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'slides', label: 'Slides', itemLabel: 'Slide', required: true,
        create: () => ({ stat: '', text: '', detail: '', icon: '', color: DEFAULT_COLOR }),
        fields: [
          row([
            emoji('icon', 'Icon'),
            text('stat', 'Number', { required: true, placeholder: '70' }),
            text('unit', 'Unit', { placeholder: '%', optional: true }),
            color('color'),
          ], '96px 1fr 1fr 1fr'),
          rich('text', 'Text', { required: true }),
          rich('detail', 'Detail'),
        ],
      },
    ],
    create: () => ({ type: 'stats', title: '', slides: [{ stat: '', text: '', detail: '', icon: '', color: DEFAULT_COLOR }] }),
  },
  {
    type: 'reveal', label: 'Reveal', group: 'step',
    description: 'Layers the user uncovers one at a time.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'layers', label: 'Layers', itemLabel: 'Layer', required: true,
        create: () => ({ title: '', icon: '', color: DEFAULT_COLOR, content: '' }),
        fields: [
          row([emoji('icon', 'Icon'), text('title', 'Title', { required: true }), color('color')], ICON_COLUMNS),
          rich('content', 'Content', { required: true }),
        ],
      },
    ],
    create: () => ({ type: 'reveal', title: '', layers: [{ title: '', icon: '', color: DEFAULT_COLOR, content: '' }] }),
  },
  {
    type: 'voices', label: 'Voices', group: 'step',
    description: 'The inner fearful voice against the confident one, pair by pair.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'pairs', label: 'Pairs', itemLabel: 'Pair', required: true,
        create: () => ({ fear: '', confidence: '' }),
        fields: [row([rich('fear', 'Fear voice', { required: true }), rich('confidence', 'Confident voice', { required: true })])],
      },
      rich('conclusion', 'Conclusion', { required: true }),
    ],
    create: () => ({ type: 'voices', title: '', pairs: [{ fear: '', confidence: '' }], conclusion: '' }),
  },
  {
    type: 'timeline', label: 'Timeline', group: 'step',
    description: 'Step-by-step timeline with a do / don\'t for each step.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'steps', label: 'Steps', itemLabel: 'Step', required: true,
        create: () => ({ time: '', label: '', icon: '', text: '', do: '', dont: '' }),
        fields: [
          row([emoji('icon', 'Icon'), text('time', 'Time', { placeholder: '0–3 сек' }), text('label', 'Label', { required: true })], '96px 1fr 2fr'),
          rich('text', 'Text'),
          row([rich('do', 'Do'), rich('dont', 'Don\'t')]),
        ],
      },
    ],
    create: () => ({ type: 'timeline', title: '', steps: [{ time: '', label: '', icon: '', text: '', do: '', dont: '' }] }),
  },
  {
    type: 'reality_check', label: 'Reality check', group: 'step',
    description: 'A fear against the reality, item by item.',
    fields: [
      TITLE,
      {
        kind: 'objectList', key: 'items', label: 'Items', itemLabel: 'Item', required: true,
        create: () => ({ fear: '', fearEmoji: '😰', reality: '', realityEmoji: '💡' }),
        fields: [
          row([emoji('fearEmoji'), rich('fear', 'Fear', { required: true })], '96px 1fr'),
          row([emoji('realityEmoji'), rich('reality', 'Reality', { required: true })], '96px 1fr'),
          text('stat', 'Stat', { placeholder: '87%', optional: true }),
        ],
      },
    ],
    create: () => ({ type: 'reality_check', title: '', items: [{ fear: '', fearEmoji: '😰', reality: '', realityEmoji: '💡' }] }),
  },
  {
    type: 'speaker', label: 'Speaker', group: 'step',
    description: 'A monologue revealed line by line.',
    fields: [TITLE, strings('texts', 'Lines', 'line', { required: true, rich: true })],
    create: () => ({ type: 'speaker', title: '', texts: [''] }),
  },
  {
    type: 'step_chat', label: 'Chat (story)', group: 'step',
    description: 'An animated chat between her and you, with no choices.',
    fields: [
      EMOJI_TITLE,
      row([
        text('characterName', 'Character name', { required: true }),
        text('characterInitial', 'Initial', { required: true }),
        color('characterColor', 'Color', OPTIONAL),
      ]),
    ],
    create: () => ({
      type: 'step_chat', emoji: '💬', title: '', characterName: '', characterInitial: '',
      messages: [{ from: 'her', text: '' }],
    }),
    issues: stepChatIssues,
  },

  // Tests
  {
    type: 'scenario', label: 'Scenario', group: 'test',
    description: 'A situation and a quote, with answer options and an explanation for each.',
    fields: [EMOJI_TITLE, rich('body', 'Situation', { required: true }), rich('scenarioQuote', 'Quote', { required: true })],
    create: () => ({
      type: 'scenario', emoji: '🎭', title: '', body: '', scenarioQuote: '',
      options: [{ id: 'a', text: '', correct: true }, { id: 'b', text: '', correct: false }],
      explanations: { a: '', b: '' },
    }),
    issues: scenarioIssues,
  },
  {
    type: 'chat_simulator', label: 'Chat simulator', group: 'test',
    description: 'A chat where the user picks a reply and sees her reaction and a verdict.',
    fields: [EMOJI_TITLE, rich('context', 'Context', { required: true, help: 'Shown before the chat starts' })],
    create: () => ({
      type: 'chat_simulator', emoji: '💬', title: '', context: '',
      initialMessages: [{ from: 'her', text: '' }],
      choices: [{ id: 'a', text: '', quality: 'good', herReply: [{ text: '', delay: 1000 }], verdict: '' }],
    }),
    issues: chatSimulatorIssues,
  },
  {
    type: 'story_mode', label: 'Story mode', group: 'test',
    description: 'A branching story: scenes, choices and good / mid / bad endings.',
    fields: [EMOJI_TITLE],
    create: () => ({
      type: 'story_mode', emoji: '📖', title: '', startScene: 'start',
      scenes: {
        start: { text: '', choices: [{ text: '', next: 'good_end' }, { text: '', next: 'bad_end' }] },
        good_end: { text: '', isEnd: true, endType: 'good', lesson: '' },
        bad_end: { text: '', isEnd: true, endType: 'bad', lesson: '' },
      },
    }),
    issues: (card) => storyIssues(card as StoryCard),
  },
  {
    type: 'quick_fire', label: 'Quick fire', group: 'test',
    description: 'Timed statements: true or myth?',
    fields: [
      EMOJI_TITLE,
      { kind: 'number', key: 'timePerQuestion', label: 'Seconds per question', required: true, min: 1, suffix: 's' },
      {
        kind: 'objectList', key: 'questions', label: 'Questions', itemLabel: 'Question', required: true,
        create: () => ({ text: '', answer: true, explain: '' }),
        fields: [
          rich('text', 'Statement', { required: true }),
          { kind: 'boolean', key: 'answer', label: 'Answer', on: 'True', off: 'Myth' },
          rich('explain', 'Explanation'),
        ],
      },
    ],
    create: () => ({ type: 'quick_fire', emoji: '⚡', title: '', timePerQuestion: 8, questions: [{ text: '', answer: true, explain: '' }] }),
  },
  {
    type: 'swipe_test', label: 'Swipe test', group: 'test',
    description: 'Swipe each statement right (correct) or left (wrong).',
    fields: [
      EMOJI_TITLE,
      {
        kind: 'objectList', key: 'items', label: 'Items', itemLabel: 'Item', required: true,
        create: () => ({ text: '', correct: true, why: '' }),
        fields: [
          rich('text', 'Statement', { required: true }),
          { kind: 'boolean', key: 'correct', label: 'Answer', on: 'Correct', off: 'Wrong' },
          rich('why', 'Why'),
        ],
      },
    ],
    create: () => ({ type: 'swipe_test', emoji: '👆', title: '', items: [{ text: '', correct: true, why: '' }] }),
  },
  {
    type: 'before_after', label: 'Before / after', group: 'test',
    description: 'A bad and a good version of the same message.',
    fields: [
      EMOJI_TITLE,
      {
        kind: 'objectList', key: 'items', label: 'Pairs', itemLabel: 'Pair', required: true,
        create: () => ({ category: '', bad: { text: '', emoji: '❌' }, good: { text: '', emoji: '✅' }, lesson: '' }),
        fields: [
          text('category', 'Category', { required: true }),
          row([
            { kind: 'object', key: 'bad', label: 'Bad version', tone: 'bad', fields: [row([emoji('emoji'), rich('text', 'Text', { required: true })], '72px 1fr')] },
            { kind: 'object', key: 'good', label: 'Good version', tone: 'good', fields: [row([emoji('emoji'), rich('text', 'Text', { required: true })], '72px 1fr')] },
          ]),
          rich('lesson', 'Lesson'),
        ],
      },
    ],
    create: () => ({
      type: 'before_after', emoji: '🔄', title: '',
      items: [{ category: '', bad: { text: '', emoji: '❌' }, good: { text: '', emoji: '✅' }, lesson: '' }],
    }),
  },
  {
    type: 'drag_rank', label: 'Drag rank', group: 'test',
    description: 'The user drags items into the right order.',
    fields: [EMOJI_TITLE, rich('instruction', 'Instruction', { required: true })],
    fieldsAfter: [rich('insight', 'Insight', { required: true, help: 'Shown after the user checks their order' })],
    create: () => ({
      type: 'drag_rank', emoji: '📊', title: '', instruction: '',
      // Stored unsolved on purpose — see dragRank.ts.
      items: [{ id: 'i2', text: '', icon: '' }, { id: 'i1', text: '', icon: '' }],
      correctOrder: ['i1', 'i2'],
      insight: '',
    }),
    issues: dragRankIssues,
  },
];

export const CARD_TYPES = Object.fromEntries(META.map((meta) => [meta.type, meta])) as Record<CardType, CardTypeMeta>;

export const CARD_TYPE_LIST: readonly CardTypeMeta[] = META;

export function cardMeta(type: string): CardTypeMeta | undefined {
  return (CARD_TYPES as Record<string, CardTypeMeta | undefined>)[type];
}

export function newCard(type: CardType): Card {
  return CARD_TYPES[type].create();
}

/** One-line label for the step list: the title, or the first text when there is none. */
export function cardSummary(card: Card): string {
  const raw = [card.title, card.body, card.characterName, card.instruction]
    .find((value) => typeof value === 'string' && value.trim()) ?? '';
  return raw.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
}

export function fieldIssues(fields: FieldSpec[], value: Record<string, any> | undefined, prefix: string): string[] {
  const source = value ?? {};
  const issues: string[] = [];
  fields.forEach((field) => {
    if (field.kind === 'row') {
      issues.push(...fieldIssues(field.fields, source, prefix));
      return;
    }
    const label = `${prefix}${field.label}`;
    const current = source[field.key];
    switch (field.kind) {
      case 'object':
        issues.push(...fieldIssues(field.fields, current, `${label} → `));
        return;
      case 'objectList': {
        const items = asArray(current);
        if (field.required && items.length === 0) issues.push(`${label}: add at least one ${field.itemLabel.toLowerCase()}`);
        items.forEach((item, i) => issues.push(...fieldIssues(field.fields, item, `${prefix}${field.itemLabel} ${i + 1} → `)));
        return;
      }
      case 'stringList':
        if (field.required && !asArray(current).some(isFilled)) issues.push(`${label}: add at least one ${field.itemLabel}`);
        return;
      case 'boolean':
        return;
      default:
        if (field.required && !isFilled(current)) issues.push(`${label} is empty`);
    }
  });
  return issues;
}

/**
 * Things to fix in a card. Advisory: they're shown in the editor but never block saving.
 * Never throws — it runs over every card, and one malformed card mustn't break the rest.
 */
export function cardIssues(card: Card): string[] {
  const meta = cardMeta(card.type);
  if (!meta) return [`Unknown card type "${card.type}"`];
  try {
    return [
      ...fieldIssues(meta.fields, card, ''),
      ...(meta.issues?.(card) ?? []),
      ...fieldIssues(meta.fieldsAfter ?? [], card, ''),
    ];
  } catch (e) {
    return [`This step's data is malformed: ${e instanceof Error ? e.message : String(e)}`];
  }
}
